/**
 * EventTimeline - Displays a chronological list of events and notes
 */

import React from 'react';
import { Loader2, AlertCircle, History } from 'lucide-react';
import { EventCard } from './EventCard';
import type { ForecastEvent } from '../../../api/events';

interface EventTimelineProps {
  events: ForecastEvent[];
  isLoading: boolean;
  isError: boolean;
  onDeleteEvent?: (eventId: string) => void;
  isDeletingEvent?: boolean;
  emptyMessage?: string;
  currentUserEmail?: string;
  chronological?: boolean; // If true, show oldest first (like chat)
}

export function EventTimeline({
  events,
  isLoading,
  isError,
  onDeleteEvent,
  isDeletingEvent,
  emptyMessage = 'No events yet',
  currentUserEmail,
  chronological = false,
}: EventTimelineProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
        <span className="ml-2 text-sm text-slate-500">Loading events...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-8 text-red-500">
        <AlertCircle className="w-5 h-5 mr-2" />
        <span className="text-sm">Failed to load events</span>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-slate-400">
        <History className="w-8 h-8 mb-2" />
        <span className="text-sm">{emptyMessage}</span>
      </div>
    );
  }

  // Sort events: chronological = oldest first (like chat), otherwise newest first
  const sortedEvents = [...events].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return chronological ? dateA - dateB : dateB - dateA;
  });

  // Group events by date
  const groupedEvents = groupEventsByDate(sortedEvents, chronological);

  // Get date keys in the right order
  const dateKeys = Object.keys(groupedEvents);

  return (
    <div className="space-y-4">
      {dateKeys.map((dateKey) => (
        <div key={dateKey}>
          {/* Date header */}
          <div className="sticky top-0 bg-white py-1 z-10">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              {dateKey}
            </span>
          </div>

          {/* Events for this date */}
          <div className="space-y-2 mt-2">
            {groupedEvents[dateKey].map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onDelete={onDeleteEvent}
                isDeleting={isDeletingEvent}
                currentUserEmail={currentUserEmail}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Group events by date for display
 * Events should already be sorted before calling this function
 */
function groupEventsByDate(events: ForecastEvent[], chronological: boolean): Record<string, ForecastEvent[]> {
  // Use Map to preserve insertion order
  const groups = new Map<string, ForecastEvent[]>();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  for (const event of events) {
    const eventDate = new Date(event.createdAt);
    eventDate.setHours(0, 0, 0, 0);

    let dateKey: string;

    if (eventDate.getTime() === today.getTime()) {
      dateKey = 'Today';
    } else if (eventDate.getTime() === yesterday.getTime()) {
      dateKey = 'Yesterday';
    } else if (eventDate.getTime() > today.getTime() - 7 * 24 * 60 * 60 * 1000) {
      // Within last 7 days - show day name
      dateKey = eventDate.toLocaleDateString('en-US', { weekday: 'long' });
    } else {
      // Older - show date
      dateKey = eventDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: eventDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      });
    }

    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(event);
  }

  // Convert Map to object while preserving order
  const result: Record<string, ForecastEvent[]> = {};
  groups.forEach((value, key) => {
    result[key] = value;
  });

  return result;
}

export default EventTimeline;
